import { z } from 'zod';

import { type AppRole, VCS_APP_ROLE, type VcsApp } from '../../../vcs/githubApp/apps';

const VCS_APPS = Object.keys(VCS_APP_ROLE) as [VcsApp, ...VcsApp[]];

export const AppParamValidator = z.object({ app: z.enum(VCS_APPS) });
export type AppParams = { params: z.input<typeof AppParamValidator> };

// GitHub appends installation_id and setup_action in snake_case to the callback URL.
// Parse and rename here so the rest of the handler can stay in camelCase.
export const InstallCallbackQueryValidator = z
  .object({
    installation_id: z.string().min(1),
    state: z.string().min(1),
    setup_action: z.string().optional(),
  })
  .transform((q) => ({
    installationId: q.installation_id,
    state: q.state,
    setupAction: q.setup_action,
  }));
export type InstallCallbackQuery = z.input<typeof InstallCallbackQueryValidator>;

export type GitHubAppInstallationResponse = {
  installed: boolean;
  accountName?: string;
  accountType?: string;
  appRole?: AppRole | null;
};

export type GitHubAppInstallationSummary = {
  connectionId: string;
  accountName: string;
  accountType: string;
  appRole: AppRole | null;
  avatarUrl: string | null;
};

export type ListInstallationsResponse = {
  results: GitHubAppInstallationSummary[];
};

export const InstallUrlRequestValidator = z.object({
  returnPath: z
    .string()
    .regex(/^\/(?![/\\])[\x20-\x7e]*$/, 'returnPath must be a same-origin absolute path')
    .optional(),
});

export type InstallUrlRequest = z.input<typeof InstallUrlRequestValidator>;

export type GitHubAppInstallUrlResponse = {
  installUrl: string;
};

/**
 * Wire DTO for the repository listing endpoint. Mirrors the legacy `Repository` field names so
 * the reused import UI consumes it directly, but stays serialization-safe: `locator` is a string
 * and matched projects are `string[]` (unlike the legacy backend `Repository`, which embeds a
 * `Locator` instance and a Sequelize `ProjectInstance`)
 *
 * `locator` is CORE-derived from the repo's clone URL.
 * `connectableProjects` and `connectedProjects` are CORE-derived from matching existing DB projects.
 * The rest pass through from vcs-service.
 */
export type VcsRepository = {
  id: string;
  title: string;
  locator: string;
  url: string;
  description: string | null;
  isFork: boolean;
  isPrivate: boolean;
  /** ISO 8601 timestamp of the last push; the import UI renders this as the repo's "updated" time. */
  updated?: string;
  branch: string;
  branches: string[];
  sshCloneURL: string;
  httpsCloneURL: string;
  connectableProjects: string[];
  connectedProjects: string[];
};

/** Query params for the paginated repository listing endpoint (raw, as Express sees them). */
export type ListRepositoriesQuery = {
  page?: string;
  pageSize?: string;
  q?: string;
  connectionId?: string;
  excludeBranches?: string;
  sort?: string;
};

export const ListRepositoriesQueryValidator = z.object({
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100)
    .default(10),
  q: z.string().optional().default(''),
  /** Scope the listing to a specific installed account; omitted means the org's first installation. */
  connectionId: z.string().min(1).optional(),
  /**
   * Pass `true` to skip the per-repo branch listing (one GitHub call each), so `branches` holds only
   * the default branch. That is all a bulk selection needs and is far cheaper across a whole account.
   * Both `'true'` and `'false'` are accepted, since that is what `qs` puts on the wire for a boolean.
   */
  excludeBranches: z.enum(['true', 'false']).transform((value) => value === 'true').optional(),
  /**
   * Order by last push. Deliberately no default: a sorted GitHub App listing goes through the
   * search API (1000-result cap, 30 req/min), so enumeration (`useAllGitHubAppRepositories`)
   * leaves this out to stay on the uncapped installation listing.
   */
  sort: z.enum(['newest', 'oldest']).optional(),
});

export type ListRepositoriesParams = z.infer<typeof ListRepositoriesQueryValidator>;

export type ListRepositoriesResponse = {
  results: VcsRepository[];
  page: number;
  pageSize: number;
  hasMore: boolean;
  total?: number;
};

export type ImportRepository = Pick<
  VcsRepository,
  'locator' | 'isPrivate' | 'branch' | 'sshCloneURL' | 'httpsCloneURL' | 'connectableProjects'
>;

/**
 * Upper bound on repos per import request, as a payload sanity guard. Batches at or above
 * `vcsImport:bulk_min_count` are handed to the background bulk job, so the bound no longer
 * protects the request from inline build queueing; it only rejects absurd payloads.
 */
export const MAX_IMPORT_REPOS = 5000;

export const ImportRepositoriesRequestValidator = z.object({
  /**
   * The account the repositories were listed under. Scopes the import so the clone token and the
   * stored `ProjectVcsConnection` reference the connection the repos actually belong to.
   */
  connectionId: z.string().min(1),
  teamIds: z.array(z.number().int().positive()).default([]),
  /**
   * Quick-import scan-method choices, applied to every newly imported project in the batch (matching
   * the legacy single-`options` import). Omitted when the org lacks the gating flags; the server also
   * strips any field the org isn't entitled to.
   */
  quickImportVendoredDetectionEnabled: z.boolean().optional(),
  quickImportSnippetDetectionEnabled: z.boolean().optional(),
  /**
   * Submit a fossabot badge PR to each newly imported public GitHub repo (the legacy
   * `send_badge_pr` option). Applies only to new imports, never to connected projects.
   */
  submitBadgePRs: z.boolean().optional(),
  /**
   * The import-all wizard's legacy "Configure Import" choices. All optional: a hand-picked
   * selection import never sends them, and their absence preserves that flow's behaviour
   * (skip notifications, per-repo visibility, org-default update hooks).
   */
  policyNotifications: z.boolean().optional(),
  policyUpdate: z.enum(['always', 'organization']).optional(),
  policyAccess: z.enum(['default', 'public', 'private']).optional(),
  repos: z
    .object({
      locator: z.string().min(1),
      isPrivate: z.boolean(),
      branch: z.string().min(1),
      sshCloneURL: z.string().min(1),
      httpsCloneURL: z.string().min(1),
      connectableProjects: z.array(z.string()),
    })
    .array()
    .min(1)
    .max(MAX_IMPORT_REPOS),
});

export type ImportRepositoriesRequest = z.input<typeof ImportRepositoriesRequestValidator>;

/** Legacy `policy_update` & `policy_access` choices from the import-all wizard, derived from the request validator. */
export type ImportPolicyUpdate = NonNullable<ImportRepositoriesRequest['policyUpdate']>;
export type ImportPolicyAccess = NonNullable<ImportRepositoriesRequest['policyAccess']>;

export type ImportRepositoriesResponse = {
  /** Repos imported inline in this request. Absent when the batch was queued in the background. */
  imported?: number;
  /** Repos handed to the background bulk-import job. Absent when the batch ran inline. */
  queued?: number;
  connected: number;
};
